import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import calendarGreyIcon from 'assets/images/icons/calendar-grey.svg';
import crossGreyIcon from 'assets/images/icons/cross-grey.svg';
import dataSourceIcon from 'assets/images/icons/data-source.svg';
import infoCircleIcon from 'assets/images/icons/info-circle.svg';
import labelIcon from 'assets/images/icons/label.svg';
import locationGreyIcon from 'assets/images/icons/location-grey.svg';
import userGreyIcon from 'assets/images/icons/user-grey.svg';
import AddCommentModal from 'pages/submissions/comment/AddCommentModal';
import AddSubmission from 'pages/submissions/AddSubmissionNew';
import AlertModal from 'components/common-components/AlertModal';
import Badge from 'components/common-components/Badge';
import ReportModal from 'pages/report/ReportModal';
import SupervisorLockedModal from 'pages/submissions/SupervisorLockedModal';
import TableComponent from 'components/table-component-new/Table';
import TaskSubmissionsHeader from './TaskSubmissionsHeader';
import { taskSubmissionHeaderList } from '../../utils';
import { CallAPI } from 'actions/General';
import { signatureStartingText } from 'config/config';
import { uploadImage } from 'pages/company/utils';

const TaskSubmissions = ({ taskId, closeModal }) => {
  const { t } = useTranslation();
  const { lang } = useSelector((state) => state.language);
  const authProfile = useSelector((state) => state.auth.profile);
  const currentUserId = authProfile.userCompanyId;

  const [taskDetail, setTaskDetail] = useState(null);
  const [formVersionDetail, setFormVersionDetail] = useState(null);
  const [allQuestionsTitleMap, setAllQuestionsTitleMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [primarySubmissions, setPrimarySubmissions] = useState([]);
  const [otherSubmissions, setOtherSubmissions] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowsPrimary, setSelectedRowsPrimary] = useState([]);
  const [filter, setFilter] = useState({});
  const [webReportInfo, setWebReportInfo] = useState(null);
  const [commentsInfo, setCommentsInfo] = useState(null);
  const [showSupervisorLockedModal, setShowSupervisorLockedModal] = useState(null);
  const [elementGlobalIdTypeMap, setElementGlobalIdTypeMap] = useState({});
  const [selectedRowForEdit, setSelectedRowForEdit] = useState(null);
  const [submissionLoader, setSubmissionLoader] = useState(false);
  const [signatureMediaIds, setSignatureMediaIds] = useState({});
  const [webPagePayloadDetails, setWebPagePayloadDetails] = useState({});

  const getTaskDetail = async (fetchFormDetails = false) => {
    const result = await CallAPI('GET_TASK_DETAIL', null, taskId);
    if (result.status) {
      setTaskDetail(result.data);
      if (fetchFormDetails) getFormVersionDetail(result.data.form?._id, result.data.formVersion);
    }
  };

  const getFormVersionDetail = async (formId, formVersion) => {
    if (formId && formVersion) {
      const apiPath = formId + '/' + formVersion;
      let result = await CallAPI('GET_FORM_VERSION_DETAIL', null, apiPath, null, null, null);
      if (result.status) {
        setFormVersionDetail(result.data);
        setWebPagePayloadDetails((prev) => ({
          ...prev,
          formId: result.data._id,
          formTitle: result.data.title,
          webPageResponseAllowed: result.data.webPageResponseAllowed,
        }));

        const questionTitlesLookup = {};
        let idTypeMap = {};

        // Mapping element global Ids with their types
        // Extracting field titles
        result.data.versions[0].elements.forEach((element) => {
          const properties = JSON.parse(element.properties);
          questionTitlesLookup[element.elementGlobalId] =
            properties.basicProperties.fieldTitle ?? '';
        });
        result.data.versions[0].supervisorElements.forEach((element) => {
          const properties = JSON.parse(element.properties);
          questionTitlesLookup[element.elementGlobalId] =
            properties.basicProperties.fieldTitle ?? '';

          idTypeMap[element.elementGlobalId] = element.elementType;
        });
        setAllQuestionsTitleMap({ ...questionTitlesLookup });
        setElementGlobalIdTypeMap({ ...idTypeMap });
        setFormVersionDetail(result.data);
      }
    }
  };

  const fetchTaskSubmissions = async () => {
    setSelectedRows([]);
    setSelectedRowsPrimary([]);
    setAllSubmissions([]);
    const params = {
      form: taskDetail.form._id,
      // formVersion: taskDetail.formVersion,
      task: taskId,
      page: 1,
      limit: 1000,
    };
    if (filter.searchText) params['userFieldSearch'] = filter.searchText;
    const result = await CallAPI('GET_ALL_FORM_SUBMISSIONS', params, null, setLoading);
    if (result.status) {
      const transformed = result.data.submissions.map((s) => transformSubmission(s));
      setAllSubmissions(transformed);
      separateSubmissions(transformed);
      attachMedia(result.data.submissions);
    }
  };

  const attachMedia = async (records) => {
    let signatureMediaIds = {};

    for (let submissionIndex = 0; submissionIndex < records.length; submissionIndex++) {
      const singleSubmission = records[submissionIndex];

      for (let valueIndex = 0; valueIndex < singleSubmission.values.length; valueIndex++) {
        const singleFormEntry = singleSubmission.values[valueIndex];

        let key = singleFormEntry.elementGlobalId;
        if (elementGlobalIdTypeMap[key] === 'signature') {
          let media = singleFormEntry.media;
          for (let mediaIndex = 0; mediaIndex < media.length; mediaIndex++) {
            const singleMedia = media[mediaIndex];

            if (
              (singleMedia && singleMedia.imageName.indexOf(signatureStartingText) === 0) ||
              singleFormEntry.elementMetaData?.includes(singleMedia._id)
            ) {
              signatureMediaIds[singleSubmission._id + '_' + key] = singleMedia._id;
            }
          }
        }
      }
    }
    setSignatureMediaIds(cloneDeep(signatureMediaIds));
  };

  const transformSubmission = (submission) => {
    const elementMetaDataMap = {};
    const mediaNotes = {};
    const mediaObj = {};
    const submissionData = {};

    for (let valueIndex = 0; valueIndex < submission.values.length; valueIndex++) {
      const singleFormEntry = submission.values[valueIndex];
      let key = singleFormEntry.elementGlobalId;

      if (
        elementGlobalIdTypeMap[key] === 'checkList' ||
        elementGlobalIdTypeMap[key] === 'response'
      ) {
        if (singleFormEntry.elementMetaData) {
          const parsedData = JSON.parse(singleFormEntry.elementMetaData);
          elementMetaDataMap[key] = parsedData.displayMap;
        }
      } else if (elementGlobalIdTypeMap[key] === 'signature') {
        if (singleFormEntry.elementMetaData) {
          const parsedData = JSON.parse(singleFormEntry.elementMetaData);
          elementMetaDataMap[key] = parsedData.signature;
        }
      }

      mediaNotes[key] = singleFormEntry.notesField || '';
      mediaObj[key] = singleFormEntry.media?.map((obj) => obj._id) || [];
      submissionData[key] = singleFormEntry.valueField ?? '';
    }

    const mediaCustomFolderId = submission.mediaCustomFolderId
      ? submission.mediaCustomFolderId
      : uuidv4();
    return {
      ...submission,
      ...submissionData,
      firstName: submission.userFirstName,
      lastName: submission.userLastName,
      form_submission_id: submission._id,
      form_version_id: submission.formVersion,
      media_custom_folder_id: mediaCustomFolderId,
      formId: submission.form,
      elementMetaDataMap,
      mediaNotes,
      mediaObj,
    };
  };

  const separateSubmissions = (submissionList) => {
    setPrimarySubmissions([]);
    setOtherSubmissions([]);
    const primarySubmissionList = [];
    const otherSubmissionList = [];

    submissionList.forEach((s) => {
      if (s._id === taskDetail.primarySubmission) primarySubmissionList.push(s);
      else if (taskDetail.submissions.includes(s._id)) otherSubmissionList.push(s);
    });

    setPrimarySubmissions(primarySubmissionList);
    setOtherSubmissions(otherSubmissionList);
  };

  const submitDataForm = async (
    formData,
    submissionId,
    versionId,
    managerSectionGlobalId,
    mediaCustomFolderId,
    sendNotificationToInspector,
    updatedFields,
  ) => {
    setSubmissionLoader(true);
    for (let index = 0; index < formData.length; index++) {
      const singleField = formData[index];
      if (singleField.type && singleField.type === 'signature') {
        if (
          signatureMediaIds[submissionId + '_' + singleField.elementGlobalId] &&
          updatedFields?.[singleField.elementGlobalId]
        ) {
          const response = await CallAPI('DELETE_MEDIA', {
            folderType: 'submissions',
            mediaId: signatureMediaIds[submissionId + '_' + singleField.elementGlobalId],
            imageName: signatureMediaIds[submissionId + '_' + singleField.elementGlobalId] + '.png',
            mediaType: 'image',
            formId: formVersionDetail._id,
            versionId: versionId,
            customFolderId: mediaCustomFolderId,
          });
          if (response.status) {
            // delete respective signature key from media
            if (singleField.elementMetaData) {
              singleField.media = singleField.media.filter(
                (item) => item !== singleField.elementMetaData,
              );
              singleField.elementMetaData = '';
            }
          }
        }
        // Upload signature if added
        if (singleField.valueField && updatedFields?.[singleField.elementGlobalId]) {
          let response = await CallAPI('GET_SUBMISSION_MEDIA_UPLOAD_URL', {
            imageName:
              signatureStartingText + submissionId + '_' + singleField.elementGlobalId + '.png',
            mediaType: 'image',
            formId: formVersionDetail._id,
            versionId: versionId,
            customFolderId: mediaCustomFolderId,
          });
          if (response.status) {
            const blob = await (await fetch(singleField.valueField)).blob();
            await uploadImage(blob, response.data, null);
            singleField.media = [...singleField.media, response.data.id];
            singleField.elementMetaData = JSON.stringify({ signature: response.data.id });
            singleField.valueField = '';
            delete singleField.type;
          }
        } else {
          singleField.valueField = '';
          delete singleField.type;
          if (singleField.elementMetaData) {
            singleField.elementMetaData = JSON.stringify({
              signature: singleField.elementMetaData,
            });
          }
        }
      }
    }

    const result = await CallAPI('UPDATE_SUBMISSION_SUPERVISOR_FIELDS', {
      form: { id: formVersionDetail._id, title: formVersionDetail.title },
      formVersion: versionId,
      submissionId: submissionId,
      managerSectionGlobalId,
      locale: lang,
      values: formData,
      sendNotificationToInspector,
    });
    setSubmissionLoader(false);
    if (result.status) {
      toast.success(t('alert_submissions_uploaded_successfully'));
      setSelectedRowForEdit(null);
      setWebReportInfo(null);
      fetchTaskSubmissions();
    } else {
      toast(result.error, {
        style: { borderRadius: '8px', background: 'red', color: '#fff' },
      });
    }
  };

  const openSubmissionReportModal = (row) => {
    setWebReportInfo({
      formId: row.form,
      submissionId: row._id,
      dataType: 'real',
      row,
      allowEditManagerSection:
        (formVersionDetail.versions[0].supervisorElements ?? []).filter(
          (element) => element.elementType !== 'status',
        ).length > 0,
    });
  };

  const editManagerSectionCallback = (row) => {
    if (row.entryStatus === 'complete_entry') {
      if (row.submissionFlowState) {
        if (row.submissionFlowState.flowState === 'section' && row.submissionFlowState.actionId)
          setSelectedRowForEdit(row);
        else setShowSupervisorLockedModal(row.submissionFlowState.flowState);
      } else setShowSupervisorLockedModal('end');
    }
  };

  const elementCommentCallback = async (row, fieldId, sectionRepetitionNumber) => {
    if (!fieldId) return;
    if (row.entryStatus === 'complete_entry') {
      setCommentsInfo({
        isTestSubmission: false,
        submissionId: row._id,
        formVersionId: row.form_version_id,
        mediaCustomFolderId: row.media_custom_folder_id,
        formTitle: row.formTitle,
        formId: row.form,
        submissionCreateByUserId: row.user,
        elementGlobalId: fieldId,
        elementTitle: allQuestionsTitleMap[fieldId] ?? '',
        ...(sectionRepetitionNumber && { sectionRepetitionNumber }),
      });
    }
  };

  const commentCallback = async (row) => {
    if (row.entryStatus === 'complete_entry')
      setCommentsInfo({
        isTestSubmission: false,
        submissionId: row._id,
        formVersionId: row.form_version_id,
        mediaCustomFolderId: row.media_custom_folder_id,
        formTitle: row.formTitle,
        formId: row.form,
        submissionCreateByUserId: row.user,
      });
  };

  const actionsCallBack = async (action, data) => {
    if (action === 'make_primary') {
      const result = await CallAPI('CHANGE_PRIMARY_SUBMISSION', { taskId, submissionId: data.id });
      if (result.status) {
        toast.success('Primary submission updated successfully');
        setSelectedRows([]);
        setSelectedRowsPrimary([]);
        getTaskDetail();
      }
    } else if (action === 'delete') {
      const payload = {
        isTestSubmission: false,
        form: { id: taskDetail.form._id, title: taskDetail.form.title },
        submissions: data.ids,
      };
      const result = await CallAPI('DELETE_BULK_SUBMISSIONS', payload);
      if (result.status) {
        fetchTaskSubmissions();
      }
    }
  };

  const onPrimaryRowsSelection = (name, value) => {
    if (name === 'all') {
      const selectedRecords = [];
      if (value)
        primarySubmissions.forEach((s) => {
          selectedRecords.push({ id: s._id, primary: true });
        });
      setSelectedRowsPrimary(selectedRecords);
    } else {
      let tempSelection = cloneDeep(selectedRowsPrimary);
      if (value) tempSelection.push({ id: name, primary: true });
      else tempSelection = tempSelection.filter((s) => s.id !== name);
      setSelectedRowsPrimary(tempSelection);
    }
  };

  const onRowsSelection = (name, value) => {
    if (name === 'all') {
      const selectedRecords = [];
      if (value)
        otherSubmissions.forEach((s) => {
          selectedRecords.push({ id: s._id });
        });
      setSelectedRows(selectedRecords);
    } else {
      let tempSelection = cloneDeep(selectedRows);
      if (value) tempSelection.push({ id: name });
      else tempSelection = tempSelection.filter((s) => s.id !== name);
      setSelectedRows(tempSelection);
    }
  };

  useEffect(() => {
    getTaskDetail(true);
  }, []);

  useEffect(() => {
    if (allSubmissions.length) separateSubmissions(allSubmissions);
  }, [taskDetail]);

  useEffect(() => {
    if (formVersionDetail) fetchTaskSubmissions();
  }, [formVersionDetail, filter]);

  useEffect(() => {
    if (selectedRowsPrimary.length === 1 && selectedRows.length === 0) {
      const foundSubmission = primarySubmissions.find(
        (sub) => sub.form_submission_id === selectedRowsPrimary[0].id,
      );
      setWebPagePayloadDetails((prev) => ({
        ...prev,
        referenceNumber: foundSubmission.referenceNumber,
        submissionDate: foundSubmission.createdAt,
        userName: `${foundSubmission.firstName} ${foundSubmission.lastName}`,
      }));
    }
  }, [selectedRowsPrimary, selectedRows]);

  return (
    <Fragment>
      <AlertModal show={true} size='xl' actionButtons={[]} noPadding>
        <div className='w-100'>
          <div className='d-flex justify-content-between border-bottom-grey-1-h-1 p-3'>
            <span>{!taskDetail ? '' : new Date(taskDetail.createdAt).toLocaleString()}</span>
            <img src={crossGreyIcon} className='cursor-pointer' onClick={() => closeModal()} />
          </div>
          {taskDetail && (
            <Fragment>
              <div className='border-bottom-grey-1-h-1 p-3'>
                <div className='d-flex gap-2 align-items-center'>
                  <Badge text={`TK-${taskDetail.referenceNumber}`} variant='grey-2' />
                  <span className='theme-size-1_2 theme-text-black-1'>{taskDetail.title}</span>
                </div>
                {taskDetail.description && (
                  <div className='mt-2'>
                    <span className='theme-text-black-1'>{taskDetail.description}</span>
                  </div>
                )}
                <div className='d-flex flex-wrap gap-3 flex-wrap align-items-center mt-3'>
                  <img src={infoCircleIcon} />
                  {taskDetail.status === 'todo' ? (
                    <Badge text='text_todo' variant='orange-2' />
                  ) : taskDetail.status === 'in_progress' ? (
                    <Badge text='text_in_progress' variant='blue-2' />
                  ) : taskDetail.status === 'completed' ? (
                    <Badge text='text_completed' variant='green-2' />
                  ) : taskDetail.status === 'closed' ? (
                    <Badge text='text_closed' variant='grey-2' />
                  ) : (
                    <></>
                  )}
                  {taskDetail.companyField && (
                    <Fragment>
                      <img src={dataSourceIcon} />
                      <Badge text={taskDetail.companyField?.fieldTitle ?? ''} variant='grey-2' />
                    </Fragment>
                  )}
                  {taskDetail.startDate && (
                    <>
                      <img src={calendarGreyIcon} />
                      <Badge
                        text={new Date(taskDetail.startDate).toLocaleString()}
                        variant='grey-2'
                      />
                    </>
                  )}
                  {(taskDetail.assignedUsers.length > 0 || taskDetail.assignedUsers.length > 0) && (
                    <Fragment>
                      <img src={userGreyIcon} />
                      <Badge
                        text={`+ ${
                          taskDetail.assignedUsers.length + taskDetail.assignedUsers.length
                        }`}
                        variant='grey-2'
                      />
                    </Fragment>
                  )}
                  {taskDetail.labels.length > 0 && (
                    <div className='d-flex gap-2'>
                      <img src={labelIcon} />
                      {taskDetail.labels.map((label, index) => (
                        <div
                          key={index}
                          style={{ backgroundColor: label.color }}
                          className='theme-text-white-1 py-1 px-2 rounded-sm'
                        >
                          {label.name}
                        </div>
                      ))}
                    </div>
                  )}
                  {taskDetail.location && (
                    <div className='d-flex gap-2'>
                      <img src={locationGreyIcon} />
                      <span>{taskDetail.location}</span>
                    </div>
                  )}
                </div>
              </div>
            </Fragment>
          )}
          <div className='p-3'>
            <TaskSubmissionsHeader
              selectedRows={[...selectedRows, ...selectedRowsPrimary]}
              actionsCallBack={actionsCallBack}
              setFilter={setFilter}
              webPagePayloadDetails={webPagePayloadDetails}
            />
            <div className='mb-2'>
              <span className='theme-size-1_2'>{t('text_primary')}</span>
            </div>
            <TableComponent
              header={taskSubmissionHeaderList}
              data={primarySubmissions}
              props={{ translateValueFunc: (val) => t(val), openSubmissionReportModal }}
              totalRecords={primarySubmissions.length}
              loader={loading}
              isFieldSelectionEnabled={false}
              isPaginationEnabled={false}
              isActionsEnabled={false}
              isSelectionEnabled={true}
              selectionKey={'_id'}
              selectedRows={selectedRowsPrimary}
              onSelection={onPrimaryRowsSelection}
            />

            <div className='mt-3 mb-2'>
              <span className='theme-size-1_2'>
                {t('text_others')} ({otherSubmissions.length})
              </span>
            </div>
            <TableComponent
              header={taskSubmissionHeaderList}
              data={otherSubmissions}
              props={{ translateValueFunc: (val) => t(val), openSubmissionReportModal }}
              totalRecords={otherSubmissions.length}
              loader={loading}
              isFieldSelectionEnabled={false}
              isPaginationEnabled={false}
              isActionsEnabled={false}
              isSelectionEnabled={true}
              selectionKey={'_id'}
              selectedRows={selectedRows}
              onSelection={onRowsSelection}
            />
          </div>
        </div>
      </AlertModal>
      {webReportInfo && (
        <ReportModal
          webReportInfo={webReportInfo}
          setWebReportInfo={setWebReportInfo}
          editManagerSection={editManagerSectionCallback}
          commentCallback={commentCallback}
          elementCommentCallback={elementCommentCallback}
          isNestedModal
        />
      )}

      {selectedRowForEdit && (
        <AddSubmission
          selectedRow={selectedRowForEdit}
          handleClose={() => setSelectedRowForEdit(null)}
          onSubmitForm={submitDataForm}
          supervisorList={[]}
          loading={submissionLoader}
          isNestedModal={!!webReportInfo}
          setShowSupervisorLockedModal={setShowSupervisorLockedModal}
        />
      )}
      {commentsInfo && (
        <AddCommentModal
          commentsInfo={commentsInfo}
          closeModal={() => setCommentsInfo(null)}
          currentUserId={currentUserId}
          isNestedModal={!!webReportInfo}
        />
      )}
      {showSupervisorLockedModal && (
        <SupervisorLockedModal
          showSupervisorLockedModal={showSupervisorLockedModal}
          setShowSupervisorLockedModal={setShowSupervisorLockedModal}
          isNestedModal={!!webReportInfo}
        />
      )}
    </Fragment>
  );
};

TaskSubmissions.propTypes = {
  taskId: PropTypes.string.isRequired,
  closeModal: PropTypes.func.isRequired,
};

export default TaskSubmissions;
